import { useState } from 'react';
import { Settings, PoundSterling, Save, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettingsTab({ pricing, fetchAllData }) {
  const [newPrice, setNewPrice] = useState(pricing.premium_annual?.toString() || "149");
  const [newMonthlyPrice, setNewMonthlyPrice] = useState(pricing.premium_monthly?.toString() || "15");
  const [annualStripePriceId, setAnnualStripePriceId] = useState(pricing.stripe_price_id || "");
  const [monthlyStripePriceId, setMonthlyStripePriceId] = useState(pricing.stripe_monthly_price_id || "");
  const [savingPrice, setSavingPrice] = useState(false);

  const handleSavePrice = async () => {
    const priceVal = parseFloat(newPrice);
    const monthlyVal = parseFloat(newMonthlyPrice);
    if (!priceVal || priceVal <= 0) {
      toast.error("Please enter a valid annual price");
      return;
    }
    if (monthlyVal && monthlyVal <= 0) {
      toast.error("Please enter a valid monthly price");
      return;
    }
    
    setSavingPrice(true);
    try {
      const payload = { 
        premium_annual: priceVal,
        premium_monthly: monthlyVal || null
      };
      if (annualStripePriceId.trim()) {
        payload.stripe_price_id = annualStripePriceId.trim();
      }
      if (monthlyStripePriceId.trim()) {
        payload.stripe_monthly_price_id = monthlyStripePriceId.trim();
      }
      
      await axios.put(`${API}/api/pricing`, payload);
      toast.success("Pricing updated successfully!");
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pricing");
    }
    setSavingPrice(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
        <Settings className="w-5 h-5" /> Pricing Settings
      </h2>
      
      <div className="max-w-xl space-y-6">
        {/* Current Pricing Display */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Annual Price</p>
            <p className="text-3xl font-bold text-gray-900">£{pricing.premium_annual}<span className="text-lg font-normal text-gray-500">/year</span></p>
          </div>
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-700 mb-1">Monthly Price</p>
            <p className="text-3xl font-bold text-amber-900">£{pricing.premium_monthly || 15}<span className="text-lg font-normal text-amber-600">/month</span></p>
          </div>
        </div>
        
        {pricing.updated_at && (
          <p className="text-xs text-gray-400">Last updated: {new Date(pricing.updated_at).toLocaleDateString()}</p>
        )}
        
        {/* Update Pricing */}
        <div className="grid grid-cols-2 gap-4">
          {/* Annual Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Annual Price (£)</label>
            <div className="relative">
              <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand text-lg"
                placeholder="149.00"
                data-testid="pricing-annual-input"
              />
            </div>
          </div>
          
          {/* Monthly Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Price (£)</label>
            <div className="relative">
              <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500" />
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={newMonthlyPrice}
                onChange={(e) => setNewMonthlyPrice(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-lg border border-amber-200 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-lg"
                placeholder="15.00"
                data-testid="pricing-monthly-input"
              />
            </div>
          </div>
        </div>
        
        {/* Stripe Price IDs */}
        <div className="p-4 border border-gray-200 rounded-lg space-y-4">
          <h3 className="text-sm font-medium text-gray-700">Stripe Price IDs (Optional)</h3>
          <p className="text-xs text-gray-500">Enter existing Stripe Price IDs if you want to link to pre-created prices. Leave blank to auto-create.</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Annual Stripe Price ID</label>
              <input
                type="text"
                value={annualStripePriceId}
                onChange={(e) => setAnnualStripePriceId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                placeholder="price_xxx..."
                data-testid="stripe-annual-id-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Monthly Stripe Price ID</label>
              <input
                type="text"
                value={monthlyStripePriceId}
                onChange={(e) => setMonthlyStripePriceId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                placeholder="price_xxx..."
                data-testid="stripe-monthly-id-input"
              />
            </div>
          </div>
        </div>
        
        {/* Save Button */}
        <button
          onClick={handleSavePrice}
          disabled={savingPrice}
          className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="save-pricing-btn"
        >
          {savingPrice ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          Save Pricing
        </button>
        <p className="text-xs text-gray-500 text-center">Changes will sync with Stripe and apply to new subscriptions</p>
        
        {/* Stripe Status */}
        <div className="p-4 border border-gray-200 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Stripe Integration</h3>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-gray-600">Connected</span>
          </div>
          {pricing.stripe_price_id && (
            <p className="text-xs text-gray-400 mt-2">Annual Price ID: {pricing.stripe_price_id}</p>
          )}
          {pricing.stripe_monthly_price_id && (
            <p className="text-xs text-gray-400 mt-1">Monthly Price ID: {pricing.stripe_monthly_price_id}</p>
          )}
        </div>
      </div>
    </div>
  );
}
